import { useState, useEffect, useCallback } from 'react';
import { getToken, onMessage, messaging } from '@/firebase/messaging';
// Dynamic Firestore imports to prevent bundle bloat
import { useAuth } from '../auth/useAuth';
import { vapidKey } from '@/firebase';

/**
 * Centralized FCM token management hook
 * Consolidates all Firebase Cloud Messaging functionality
 */
export const useFCM = () => {
  const { user } = useAuth();
  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);

  // Check if FCM is supported in this environment
  useEffect(() => {
    const checkSupport = async () => {
      const messagingInstance = await messaging();
      const supported = 'serviceWorker' in navigator &&
                       'Notification' in window &&
                       messagingInstance !== null;
      setIsSupported(supported);

      if (supported) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      throw new Error('FCM not supported in this environment');
    }

    setLoading(true);
    setError(null);

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        await refreshToken();
        return true;
      } else {
        setError('Notification permission denied');
        return false;
      }
    } catch (err) {
      const errorMessage = 'Failed to request notification permission';
      setError(errorMessage);
      console.error('Permission request error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Get or refresh FCM token
  const refreshToken = useCallback(async () => {
    const messagingInstance = await messaging();
    if (!isSupported || !messagingInstance) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const currentToken = await getToken(messagingInstance, { vapidKey });

      if (currentToken) {
        setToken(currentToken);

        // Store token if user is authenticated
        if (user) {
          await storeTokenInFirestore(user.uid, currentToken);
        }

        return currentToken;
      } else {
        setError('Failed to generate FCM token');
        return null;
      }
    } catch (err) {
      const errorMessage = 'Failed to get FCM token';
      setError(errorMessage);
      // Don't spam console with FCM errors in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('FCM initialization failed in development (this is usually normal):', err.message);
      } else {
        console.error('Token refresh error:', err);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, isSupported]);

  // Store FCM token in appropriate Firestore collection
  const storeTokenInFirestore = useCallback(async (userUid, fcmToken) => {
    try {
      // Check if user is an expert first
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const { getFirestoreLite } = await import('@/firebase/firestore-lite');
      const expertDocRef = doc(getFirestoreLite(), 'expertApplications', userUid);
      const expertSnap = await getDoc(expertDocRef);

      if (expertSnap.exists()) {
        // Store in expertApplications collection
        const { setDoc } = await import('firebase/firestore/lite');
        await setDoc(expertDocRef, { fcmToken }, { merge: true });
      } else {
        // Store in userProfiles collection
        const { doc: docUser, setDoc } = await import('firebase/firestore/lite');
        const { getFirestoreLite: getFirestoreLiteUser } = await import('@/firebase/firestore-lite');
        const userDocRef = docUser(getFirestoreLiteUser(), 'userProfiles', userUid);
        await setDoc(userDocRef, { fcmToken }, { merge: true });
      }
    } catch (err) {
      console.error('Error storing FCM token:', err);
      throw new Error('Failed to store FCM token');
    }
  }, []);

  // Clear FCM token (for logout)
  const clearToken = useCallback(async () => {
    setToken(null);
    setError(null);

    if (user) {
      try {
        // Remove token from Firestore
        const { doc, getDoc } = await import('firebase/firestore/lite');
        const { getFirestoreLite: getFirestoreLite2 } = await import('@/firebase/firestore-lite');
        const expertDocRef = doc(getFirestoreLite2(), 'expertApplications', user.uid);
        const expertSnap = await getDoc(expertDocRef);

        if (expertSnap.exists()) {
          const { setDoc } = await import('firebase/firestore/lite');
          await setDoc(expertDocRef, { fcmToken: null }, { merge: true });
        } else {
          const { doc: docUser, setDoc: setDocUser } = await import('firebase/firestore/lite');
          const { getFirestoreLite: getFirestoreLite3 } = await import('@/firebase/firestore-lite');
          const userDocRef = docUser(getFirestoreLite3(), 'userProfiles', user.uid);
          await setDocUser(userDocRef, { fcmToken: null }, { merge: true });
        }
      } catch (err) {
        console.error('Error clearing FCM token:', err);
      }
    }
  }, [user]);

  // Initialize FCM token when user logs in
  useEffect(() => {
    const initializeFCM = async () => {
      if (user && isSupported && permission === 'granted') {
        await refreshToken();
      }
    };

    initializeFCM();
  }, [user, isSupported, permission, refreshToken]);

  // Setup foreground message listener
  useEffect(() => {
    const setupMessageListener = async () => {
      const messagingInstance = await messaging();
      if (!isSupported || !messagingInstance) {return;}

      const unsubscribe = onMessage(messagingInstance, (payload) => {
        console.log('Foreground message received:', payload);

        // You can dispatch custom events here for components to listen to
        const messageEvent = new CustomEvent('fcmMessage', {
          detail: payload
        });
        window.dispatchEvent(messageEvent);
      });

      return unsubscribe;
    };

    setupMessageListener();
  }, [isSupported]);

  // Auto-retry logic for failed token requests
  const retryTokenRequest = useCallback(async () => {
    if (error && isSupported) {
      return await refreshToken();
    }
    return null;
  }, [error, isSupported, refreshToken]);

  // Check if notifications are enabled
  const isNotificationsEnabled = permission === 'granted' && !!token;

  return {
    // State
    token,
    permission,
    loading,
    error,
    isSupported,
    isNotificationsEnabled,

    // Actions
    requestPermission,
    refreshToken,
    clearToken,
    retryTokenRequest,
  };
};
